if (!customElements.get('sigma-reveal')) {
  customElements.define('sigma-reveal', class extends HTMLElement {
    connectedCallback() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('is-visible');
        return;
      }
      this.observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-visible');
        this.observer.disconnect();
      }, {threshold: .22, rootMargin: '0px 0px -8%'});
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}

if (!customElements.get('sigma-flick')) {
  customElements.define('sigma-flick', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const viewport = this.querySelector('.sigma-flick-rail__viewport');
      const card = this.querySelector('.sigma-flick-card');
      const buttons = [...this.querySelectorAll('[data-direction]')];
      const progress = this.querySelector('.sigma-flick-rail__progress span');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      if (!viewport || !card) return;

      const update = () => {
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        buttons.forEach((button) => {
          button.disabled = Number(button.dataset.direction) < 0
            ? viewport.scrollLeft < 2
            : viewport.scrollLeft >= maximum - 2;
        });
        if (progress) progress.style.transform = `scaleX(${maximum ? viewport.scrollLeft / maximum : 0})`;
      };
      const step = (direction) => viewport.scrollBy({
        left: direction * (card.offsetWidth + 16),
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });

      buttons.forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => step(Number(button.dataset.direction)), {signal});
      });
      viewport.addEventListener('scroll', update, {passive: true, signal});
      viewport.addEventListener('keydown', (event) => {
        if (event.target !== viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        step(event.key === 'ArrowLeft' ? -1 : 1);
      }, {signal});
      viewport.addEventListener('wheel', (event) => {
        if (event.ctrlKey) return;
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        const maximum = viewport.scrollWidth - viewport.clientWidth;
        const canMove = delta < 0 ? viewport.scrollLeft > 0 : viewport.scrollLeft < maximum - 1;
        if (!canMove || Math.abs(delta) < 1) return;
        event.preventDefault();
        viewport.scrollLeft += delta;
      }, {passive: false, signal});

      let drag = null;
      let dragged = false;
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = {x: event.clientX, left: viewport.scrollLeft, id: event.pointerId};
        dragged = false;
      }, {signal});
      viewport.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const delta = event.clientX - drag.x;
        if (Math.abs(delta) > 6) {
          dragged = true;
          viewport.setPointerCapture(drag.id);
          viewport.classList.add('is-dragging');
        }
        if (dragged) viewport.scrollLeft = drag.left - delta;
      }, {signal});
      const release = () => { drag = null; viewport.classList.remove('is-dragging'); };
      window.addEventListener('pointerup', release, {signal});
      viewport.addEventListener('pointercancel', release, {signal});
      viewport.addEventListener('dragstart', (event) => event.preventDefault(), {signal});
      viewport.addEventListener('click', (event) => {
        if (!dragged) return;
        event.preventDefault();
        dragged = false;
      }, {capture: true, signal});

      this.resize = new ResizeObserver(update);
      this.resize.observe(viewport);
      update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  });
}

if (!customElements.get('sigma-lookbook')) {
  customElements.define('sigma-lookbook', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.media = matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');
      this.sticky = this.querySelector('.sigma-lookbook__sticky');
      this.track = this.querySelector('.sigma-lookbook__track');
      this.meter = this.querySelector('.sigma-lookbook__meter span');
      if (!this.sticky || !this.track) return;

      let frame = 0;
      const render = () => {
        frame = 0;
        if (!this.media.matches) return;
        const rect = this.getBoundingClientRect();
        const range = Math.max(1, this.offsetHeight - this.sticky.offsetHeight);
        const amount = Math.min(1, Math.max(0, (92 - rect.top) / range));
        const distance = Math.max(0, this.track.scrollWidth - this.sticky.clientWidth);
        this.track.style.transform = `translate3d(${-distance * amount}px, 0, 0)`;
        if (this.meter) this.meter.style.transform = `scaleX(${amount})`;
      };
      const requestRender = () => {
        if (frame) return;
        frame = requestAnimationFrame(render);
      };
      const configure = () => {
        this.classList.toggle('is-active', this.media.matches);
        if (!this.media.matches) {
          this.track.style.transform = '';
          if (this.meter) this.meter.style.transform = '';
        }
        requestRender();
      };

      window.addEventListener('scroll', requestRender, {passive: true, signal: this.abort.signal});
      document.querySelector('.page-wrapper')?.addEventListener('scroll', requestRender, {passive: true, signal: this.abort.signal});
      window.addEventListener('resize', requestRender, {passive: true, signal: this.abort.signal});
      this.media.addEventListener('change', configure, {signal: this.abort.signal});
      this.resize = new ResizeObserver(requestRender);
      this.resize.observe(this.track);
      configure();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
    }
  });
}
